import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Code, Cloud, Users, TrendingUp, Rocket, Heart, Zap, Globe } from "lucide-react";
import { motion } from "framer-motion";
import teamImage from "@/assets/team-collaboration.jpg";

const Careers = () => {
  const roles = [
    { title: "Senior AI Engineer", department: "Engineering", location: "Cologne / Remote", type: "Full-time" },
    { title: "DevOps Engineer", department: "Infrastructure", location: "Remote", type: "Full-time" },
    { title: "Enterprise Sales Manager", department: "Sales", location: "Cologne", type: "Full-time" },
    { title: "Product Marketing Lead", department: "Marketing", location: "Cologne / Remote", type: "Full-time" },
  ];

  const benefits = [
    { 
      icon: Code, 
      title: "Cutting-Edge Tech", 
      description: "Work with the latest AI and voice technology stack",
      gradient: "from-gradient-pink/20 to-gradient-purple/20"
    },
    { 
      icon: Cloud, 
      title: "Flexible Work", 
      description: "Remote-first culture with hybrid options in Cologne",
      gradient: "from-gradient-purple/20 to-gradient-blue/20"
    },
    { 
      icon: Users, 
      title: "Great Team", 
      description: "Collaborative environment with talented professionals",
      gradient: "from-gradient-blue/20 to-gradient-cyan/20"
    },
    { 
      icon: TrendingUp, 
      title: "Growth Opportunities", 
      description: "Fast-growing startup with career advancement",
      gradient: "from-gradient-cyan/20 to-gradient-pink/20"
    },
  ];

  const perks = [
    { icon: Rocket, text: "Competitive salary & equity" },
    { icon: Heart, text: "Comprehensive health insurance" },
    { icon: Zap, text: "Learning & development budget" },
    { icon: Globe, text: "Work from anywhere policy" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-shadow-glow">
              Join Our <span className="gradient-brand-text">Team</span>
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              Help us build the future of enterprise AI voice agents. We're looking for talented individuals who are passionate about AI, customer experience, and innovation.
            </p>
          </motion.div>

          <div className="relative rounded-2xl overflow-hidden mb-16">
            <img 
              src={teamImage} 
              alt="Team collaboration" 
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent flex items-end justify-center p-12">
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-4">Build the Future with Us</h2>
                <p className="text-lg text-foreground/80">We're on a mission to transform enterprise communication</p>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">Why Holionix?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`p-6 card-dark text-center hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${benefit.gradient}`}>
                    <div className="w-12 h-12 rounded-lg gradient-brand flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-foreground/70">{benefit.description}</p>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">Perks & Benefits</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {perks.map((perk, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 card-dark p-4 rounded-lg"
                >
                  <perk.icon className="w-6 h-6 text-accent flex-shrink-0" />
                  <span className="text-sm font-medium">{perk.text}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Open Positions</h2>
            <div className="space-y-4">
              {roles.map((role, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 card-dark gradient-border hover:glow-effect transition-all duration-300">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">{role.title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-foreground/70">
                          <span className="px-3 py-1 rounded-full bg-accent/20 text-accent">{role.department}</span>
                          <span className="px-3 py-1 rounded-full bg-foreground/10">{role.location}</span>
                          <span className="px-3 py-1 rounded-full bg-foreground/10">{role.type}</span>
                        </div>
                      </div>
                      <Button className="gradient-brand text-white hover:opacity-90 w-full md:w-auto">
                        Apply Now
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            <Card className="mt-12 p-10 card-dark gradient-border text-center glow-effect">
              <h3 className="text-2xl font-bold mb-4">Don't see your role?</h3>
              <p className="text-foreground/70 mb-6">
                We're always looking for talented people. Send us your CV and let us know why you'd be a great fit for Holionix.
              </p>
              <Button className="gradient-brand text-white hover:opacity-90">
                Send Application
              </Button>
            </Card>
          </div>

          <div className="mt-20 text-center">
            <p className="text-foreground/70 mb-2">📍 Based in Cologne, Germany 🇩🇪</p>
            <p className="text-foreground/70">Remote-first with hybrid options</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
